import { Reducer } from 'redux'
import { createFilter } from 'redux-persist-transform-filter'

import { RequestsActionTypes, RequestsState } from './types'

export const FILTER_PERSISTOR = createFilter(
  'requests',
  null,
  ['draft', 'filterRequestNumber', 'filterStatus'],
  'whitelist'
)

const INITIAL_STATE: RequestsState = {
  draft: null,
  filterRequestNumber: '',
  filterStatus: [],
  fullRequest: null,
  fullRequestError: '',
  fullRequestLoading: false,
  fullRequestReloading: false,
  myRequests: [],
  myRequestsError: '',
  myRequestsLoading: false,
  myRequestsReloading: false,
  request_totals: {
    total_approvals: 0,
    total_audits: 0,
    total_closings: 0,
    total_conferences: 0,
    total_mocks: 0,
    total_requests: 0,
    total_toodopay: 0
  },
  saveFullRequestError: '',
  saveFullRequestLoading: false,
  saveFullRequestSuccess: '',
  setStatusError: '',
  setStatusLoading: false,
  totalResults: 0
}

const reducer: Reducer<RequestsState> = (
  state = INITIAL_STATE,
  { payload, type }
) => {
  switch (type) {
    /**
     * GET_FULL_REQUEST
     */
    case RequestsActionTypes.GET_FULL_REQUEST:
      return {
        ...state,
        fullRequest: payload.reload ? state.fullRequest : null,
        fullRequestError: '',
        fullRequestLoading: !payload.reload,
        fullRequestReloading: payload.reload
      }
    case RequestsActionTypes.GET_FULL_REQUEST_SUCCESS:
      return {
        ...state,
        fullRequest: payload.fullRequest,
        fullRequestLoading: false,
        fullRequestReloading: false
      }
    case RequestsActionTypes.GET_FULL_REQUEST_FAILURE:
      return {
        ...state,
        fullRequestError: payload.message,
        fullRequestLoading: false,
        fullRequestReloading: false
      }

    /**
     * GET_MY_REQUESTS
     */
    case RequestsActionTypes.GET_MY_REQUESTS:
      const filterRequestNumber: string =
        typeof payload.options.request_numbers !== 'undefined'
          ? payload.options.request_numbers
          : state.filterRequestNumber
      const filterStatus: string[] =
        typeof payload.options.filterStatus !== 'undefined'
          ? payload.options.filterStatus
          : state.filterStatus

      return {
        ...state,
        filterRequestNumber,
        filterStatus,
        myRequestsError: '',
        myRequestsLoading: !payload.options.reload,
        myRequestsReloading: payload.options.reload
      }
    case RequestsActionTypes.GET_MY_REQUESTS_SUCCESS:
      return {
        ...state,
        myRequests: payload.myRequests,
        request_totals: payload.request_totals,
        totalResults: payload.totalResults,
        myRequestsLoading: false,
        myRequestsReloading: false
      }
    case RequestsActionTypes.GET_MY_REQUESTS_FAILURE:
      return {
        ...state,
        myRequestsError: payload.message,
        myRequestsLoading: false,
        myRequestsReloading: false
      }

    /**
     * SAVE_FULL_REQUEST
     */
    case RequestsActionTypes.SAVE_FULL_REQUEST:
      return {
        ...state,
        saveFullRequestError: '',
        saveFullRequestLoading: true,
        saveFullRequestSuccess: ''
      }
    case RequestsActionTypes.SAVE_FULL_REQUEST_SUCCESS:
      return {
        ...state,
        saveFullRequestLoading: false,
        saveFullRequestSuccess: payload.message
      }
    case RequestsActionTypes.SAVE_FULL_REQUEST_FAILURE:
      return {
        ...state,
        saveFullRequestError: payload.message,
        saveFullRequestLoading: false
      }
    case RequestsActionTypes.RESET_SAVE_FULL_REQUEST:
      return {
        ...state,
        saveFullRequestError: '',
        saveFullRequestLoading: false,
        saveFullRequestSuccess: ''
      }
    /**
     * Advances
     */
    case RequestsActionTypes.SAVE_REQUEST_TICKETS:
      return {
        ...state,
        saveFullRequestError: '',
        saveFullRequestLoading: true
      }
    case RequestsActionTypes.SAVE_REQUEST_TICKETS_SUCCESS:
      return {
        ...state,
        fullRequest: {
          ...state.fullRequest,
          request_tickets: payload.request_tickets
        },
        saveFullRequestLoading: false
      }
    case RequestsActionTypes.SAVE_REQUEST_TICKETS_FAILURE:
      return {
        ...state,
        saveFullRequestError: payload.message,
        saveFullRequestLoading: false
      }
    /**
     * Itineraries
     */
    case RequestsActionTypes.SAVE_REQUEST_ITINERARY:
      return {
        ...state,
        saveFullRequestError: '',
        saveFullRequestLoading: true
      }
    case RequestsActionTypes.SAVE_REQUEST_ITINERARY_SUCCESS:
      return {
        ...state,
        fullRequest: {
          ...state.fullRequest,
          request_itineraries: payload.itineraries
        },
        saveFullRequestLoading: false
      }
    case RequestsActionTypes.SAVE_REQUEST_ITINERARY_FAILURE:
      return {
        ...state,
        saveFullRequestError: payload.message,
        saveFullRequestLoading: false
      }
    /**
     * Standard Assignments
     */
    case RequestsActionTypes.SAVE_REQUEST_STANDARD_ASSIGNMENTS:
      return {
        ...state,
        saveFullRequestError: '',
        saveFullRequestLoading: true
      }
    case RequestsActionTypes.SAVE_REQUEST_STANDARD_ASSIGNMENTS_SUCCESS:
      return {
        ...state,
        fullRequest: {
          ...state.fullRequest,
          request_standard_assignments: payload.request_standard_assignments
        },
        saveFullRequestLoading: false
      }
    case RequestsActionTypes.SAVE_REQUEST_STANDARD_ASSIGNMENTS_FAILURE:
      return {
        ...state,
        saveFullRequestError: payload.message,
        saveFullRequestLoading: false
      }
    /**
     * SET_UNDERWAY_STATUS
     */
    case RequestsActionTypes.SET_UNDERWAY_STATUS:
      return {
        ...state,
        setStatusError: '',
        setStatusLoading: true
      }
    case RequestsActionTypes.SET_UNDERWAY_STATUS_SUCCESS:
      return {
        ...state,
        setStatusLoading: false
      }
    case RequestsActionTypes.SET_UNDERWAY_STATUS_FAILURE:
      return {
        ...state,
        setStatusError: payload.message,
        setStatusLoading: false
      }

    /**
     * DRAFT
     */
    case RequestsActionTypes.SET_DRAFT:
      return { ...state, draft: payload.draft }

    /**
     * SET_FULL_REQUEST
     */
    case RequestsActionTypes.SET_FULL_REQUEST:
      return { ...state, fullRequest: payload.request }

    /**
     * SET_RECEIPT_CONFERENCE_CHECK
     */
    case RequestsActionTypes.SET_RECEIPT_CONFERENCE_CHECK:
      const { checked, ticket_id } = payload
      return {
        ...state,
        fullRequest: {
          ...state.fullRequest,
          request_tickets: state.fullRequest.request_tickets.reduce(
            (acc, ticket) =>
              ticket.request_ticket_id !== ticket_id
                ? [...acc, ticket]
                : [
                    ...acc,
                    {
                      ...ticket,
                      request_ticket_receipts:
                        ticket.request_ticket_receipts.map(receipt => ({
                          ...receipt,
                          checked_receipt: checked
                        }))
                    }
                  ],
            []
          )
        }
      }

    /**
     * SET_REQUEST_TOTALS
     */
    case RequestsActionTypes.SET_REQUEST_TOTALS:
      return { ...state, request_totals: payload.request_totals }

    default:
      return state
  }
}

export default reducer
